import React, { useState, useEffect, useRef } from "react";

export function Globe({ dark }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  // Parallax: the globe sits in the scrolling content, so moving it by
  // two thirds of the scroll makes it drift at a third of the feed's speed.
  useEffect(() => {
    const el = wrapRef.current;
    const scroller = el?.parentElement;
    if (!el || !scroller) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; el.style.transform = `translate(-50%, calc(-50% + ${scroller.scrollTop * 0.66}px))`; });
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { scroller.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  const rotRef = useRef(0);
  const rafRef = useRef(null);
  const [ready, setReady] = useState(false);
  const geoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadAndDraw() {
      try {
        // Fetch low-res world geo from CDN
        const res = await fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json");
        const world = await res.json();
        if (cancelled) return;
        geoRef.current = world;
        setReady(true);
      } catch(e) {
        console.error("Globe fetch failed", e);
      }
    }
    loadAndDraw();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const R = W * 0.46;
    const cx = W / 2, cy = H / 2;
    const goldColor = dark ? "rgba(226,221,159," : "rgba(180,170,80,";
    const world = geoRef.current;

    // Convert topojson to geojson features
    // Manual topojson decode for countries
    function decodeArcs(topology, arcs) {
      const scale = topology.transform?.scale || [1,1];
      const translate = topology.transform?.translate || [0,0];
      return arcs.map(arcIdx => {
        const reversed = arcIdx < 0;
        const idx = reversed ? ~arcIdx : arcIdx;
        const arc = topology.arcs[idx];
        let x = 0, y = 0;
        const pts = arc.map(([dx, dy]) => {
          x += dx; y += dy;
          return [x * scale[0] + translate[0], y * scale[1] + translate[1]];
        });
        return reversed ? pts.reverse() : pts;
      });
    }

    function topoToGeo(topology, objectName) {
      const obj = topology.objects[objectName];
      return obj.geometries.map(geom => {
        // A ring is a chain of arcs. Each arc decodes to its own point list,
        // so the ring has to be flattened into one list before it's drawn.
        // Without this the drawer read whole arcs as [lon, lat] pairs and
        // painted chords across the globe.
        if (geom.type === "Polygon") {
          return { type:"Polygon", arcs: geom.arcs.map(ring => decodeArcs(topology, ring).flat()) };
        } else if (geom.type === "MultiPolygon") {
          return { type:"MultiPolygon", arcs: geom.arcs.map(poly => poly.map(ring => decodeArcs(topology, ring).flat())) };
        }
        return null;
      }).filter(Boolean);
    }

    // Project lon/lat to canvas x/y given rotation
    function project(lon, lat, rotY) {
      const lam = (lon + rotY) * Math.PI / 180;
      const phi = lat * Math.PI / 180;
      // Orthographic projection
      const cosC = Math.sin(0) * Math.sin(phi) + Math.cos(0) * Math.cos(phi) * Math.cos(lam);
      if (cosC < 0) return null; // behind globe
      const x = cx + R * Math.cos(phi) * Math.sin(lam);
      const y = cy - R * (Math.cos(0) * Math.sin(phi) - Math.sin(0) * Math.cos(phi) * Math.cos(lam));
      return [x, y];
    }

    function drawPath(ctx, ringPts, rotY) {
      let started = false, broken = false;
      ctx.beginPath();
      for (const [lon, lat] of ringPts) {
        const p = project(lon, lat, rotY);
        if (!p) { if (started) broken = true; started = false; continue; }
        if (!started) { ctx.moveTo(p[0], p[1]); started = true; }
        else ctx.lineTo(p[0], p[1]);
      }
      // Only close an outline that stayed on the visible side. Closing one
      // that dipped behind the globe draws a straight edge across it.
      if (!broken) ctx.closePath();
    }

    const countries = topoToGeo(world, "countries");

    function draw(rot) {
      ctx.clearRect(0, 0, W, H);

      // Globe base sphere
      const gradient = ctx.createRadialGradient(cx - R*0.25, cy - R*0.25, R*0.05, cx, cy, R);
      if (dark) {
        gradient.addColorStop(0, "rgba(60,58,40,0.18)");
        gradient.addColorStop(1, "rgba(20,18,8,0.12)");
      } else {
        gradient.addColorStop(0, "rgba(240,235,180,0.15)");
        gradient.addColorStop(1, "rgba(200,190,120,0.06)");
      }
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Sphere edge
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = goldColor + "0.35)";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Graticule (lat/lon grid) — kept faint; at higher opacity the grid
      // lines read as noise behind the feed's filter chips
      ctx.strokeStyle = goldColor + "0.08)";
      ctx.lineWidth = 0.6;
      // Latitude lines
      for (let lat = -80; lat <= 80; lat += 20) {
        ctx.beginPath();
        let started = false;
        for (let lon = -180; lon <= 180; lon += 2) {
          const p = project(lon, lat, rot);
          if (!p) { started = false; continue; }
          if (!started) { ctx.moveTo(p[0], p[1]); started = true; }
          else ctx.lineTo(p[0], p[1]);
        }
        ctx.stroke();
      }
      // Longitude lines
      for (let lon = -180; lon < 180; lon += 20) {
        ctx.beginPath();
        let started = false;
        for (let lat = -90; lat <= 90; lat += 2) {
          const p = project(lon, lat, rot);
          if (!p) { started = false; continue; }
          if (!started) { ctx.moveTo(p[0], p[1]); started = true; }
          else ctx.lineTo(p[0], p[1]);
        }
        ctx.stroke();
      }

      // Country fills + outlines
      for (const country of countries) {
        const polys = country.type === "Polygon" ? [country.arcs] : country.arcs;
        for (const poly of polys) {
          const outer = poly[0];
          drawPath(ctx, outer, rot);
          ctx.fillStyle = goldColor + "0.18)";
          ctx.fill();
          ctx.strokeStyle = goldColor + "0.55)";
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }
    }

    // Static single draw. This used to spin via requestAnimationFrame —
    // a full-canvas country redraw at 60fps, forever, behind the feed.
    // On a phone that rAF loop competes with feed scrolling on the main
    // thread; the decoration isn't worth the jank budget.
    draw(rotRef.current);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ready, dark]);

  return (
    <div ref={wrapRef} style={{
      position:"absolute",
      top:"50%", left:"50%",
      transform:"translate(-50%,-50%)",
      width:"min(90vw,680px)", height:"min(90vw,680px)",
      pointerEvents:"none", zIndex:0,
      opacity: dark ? 0.2 : 0.14,
      willChange:"transform",
    }}>
      {!ready && (
        <div style={{width:"100%",height:"100%",borderRadius:"50%",border:"1px solid rgba(226,221,159,0.15)"}}/>
      )}
      {ready && (
        <canvas ref={canvasRef} width={680} height={680}
          style={{width:"100%",height:"100%",borderRadius:"50%"}}
        />
      )}
    </div>
  );
}
